import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Stack,
    Box,
    IconButton,
    Fab,
    Paper,
    Slide,
    useMediaQuery,
} from "@mui/material";
import { Cookie, Close } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";

import { ConsentState } from "./cookieTypes";
import { cookieCategories } from "./cookieConfig";
import { CategorySettings } from "./cookieBanner/cookieCategorySettings";
import { ProviderListDialog } from "./cookieBanner/cookieProviderListDialog";
import { useCookieConsent } from "./cookieContext";

const DEFAULT_CONSENT: ConsentState = {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
};

const hasAnyCookies = () =>
    cookieCategories.some((category) => category.cookies?.length > 0);


export function CookieConsent() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const { consent, setConsent } = useCookieConsent();

    const [open, setOpen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showProviderList, setShowProviderList] = useState(false);
    const [tempConsent, setTempConsent] = useState<ConsentState>(DEFAULT_CONSENT);

    const noCookiesMode = !hasAnyCookies();
    const hasDecided = consent !== null

    useEffect(() => {
        if (!hasDecided) {
            setOpen(true);
            setTempConsent(DEFAULT_CONSENT);
        } else if (hasDecided) {
            setOpen(false)
            setTempConsent(consent);
        }
    }, [consent]);

    const save = (value: ConsentState) => {
        setConsent(value);
        setOpen(false);
        setShowSettings(false);
    };

    const toggle = (key: keyof ConsentState) => {
        if (key === "necessary") return;
        setTempConsent((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const acceptAll = () =>
        save({
            necessary: true,
            functional: true,
            analytics: true,
            marketing: true,
        });

    const rejectAll = () =>
        save({
            necessary: true,
            functional: false,
            analytics: false,
            marketing: false,
        });

    return (
        <>
            {!open && (
                <Fab
                    size="small"
                    onClick={() => setOpen(true)}
                    sx={{ position: "fixed", bottom: 16, left: 16, zIndex: 1300 }}
                >
                    <Cookie />
                </Fab>
            )}

            <Slide direction="up" in={open} mountOnEnter unmountOnExit>
                <Paper
                    elevation={8}
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1400,
                        p: 2,
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                    >
                        <Typography color="text.secondary">
                            {noCookiesMode
                                ? "Wir benutzen keine Cookies."
                                : "Wir verwenden Cookies, um die grundlegende Funktionalität unserer Webseite sicherzustellen und Ihre Nutzererfahrung zu verbessern."}
                        </Typography>

                        <Stack direction="row" spacing={1}>
                            {noCookiesMode ? (
                                <Button size="small" variant="contained" onClick={rejectAll}>
                                    Verstanden!
                                </Button>
                            ) : (
                                <>
                                    <Button size="small" onClick={rejectAll}>
                                        Ablehnen
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setShowSettings(true)}
                                    >
                                        Einstellungen
                                    </Button>
                                    <Button size="small" variant="contained" onClick={acceptAll}>
                                        Alle akzeptieren
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </Stack>
                </Paper>
            </Slide>

            {!noCookiesMode && (
                <Dialog
                    open={showSettings}
                    onClose={() => setShowSettings(false)}
                    fullScreen={isMobile}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
                        Cookie Präferenzen
                        <Box sx={{ flexGrow: 1 }} />
                        <IconButton onClick={() => setShowSettings(false)}>
                            <Close />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers>
                        <Stack spacing={3}>
                            {cookieCategories.map((category) => (
                                <CategorySettings
                                    key={category.id}
                                    category={category}
                                    checked={tempConsent[category.id as keyof ConsentState]}
                                    onToggle={() =>
                                        toggle(category.id as keyof ConsentState)
                                    }
                                />
                            ))}

                            <Box sx={{ pt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setShowProviderList(true)}
                                >
                                    Liste aller Anbieter anzeigen
                                </Button>
                            </Box>
                        </Stack>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setShowSettings(false)}>Zurück</Button>
                        <Button variant="contained" onClick={() => save(tempConsent)}>
                            Präferenzen speichern
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <ProviderListDialog
                open={showProviderList}
                onClose={() => setShowProviderList(false)}
            />
        </>
    );
}
